import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { rulesAPI } from '../services/api';

interface Rule {
  id: number;
  name: string;
  accountCount: number;
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [patternOpen, setPatternOpen] = useState(location.pathname.startsWith('/pattern'));
  const [accountsOpen, setAccountsOpen] = useState(location.pathname.startsWith('/accounts'));
  const [aBookOpen, setABookOpen] = useState(location.pathname.startsWith('/a-book'));
  const [statsOpen, setStatsOpen] = useState(location.pathname.startsWith('/statistics'));
  const [rules, setRules] = useState<Rule[]>([]);

  useEffect(() => {
    rulesAPI.getAll().then(res => setRules(res.data)).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const isActive = (path: string) => location.pathname === path;
  const isPatternActive = location.pathname.startsWith('/pattern');
  const isAccountsActive = location.pathname.startsWith('/accounts');

  return (
    <div style={styles.container}>
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={styles.logo}>AB</div>
          <div>
            <div style={styles.logoTitle}>AB Book</div>
            <div style={styles.logoSub}>Hybrid System</div>
          </div>
        </div>

        <nav style={styles.nav}>
          <button
            style={isActive('/dashboard') ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem}
            onClick={() => navigate('/dashboard')}
          >
            <span style={styles.navIcon}>📊</span>
            <span>대시보드</span>
          </button>

          {/* 계좌 관리 - 룰별 서브메뉴 */}
          <div>
            <button
              style={{ ...styles.navItem, ...(isAccountsActive ? styles.navItemActive : {}) }}
              onClick={() => { setAccountsOpen(!accountsOpen); if (!accountsOpen) navigate('/accounts'); }}
            >
              <span style={styles.navIcon}>👥</span>
              <span style={{ flex: 1 }}>계좌 관리</span>
              <span style={{ fontSize: '10px', opacity: 0.5 }}>{accountsOpen ? '▲' : '▼'}</span>
            </button>
            {accountsOpen && (
              <div style={styles.subMenu}>
                <button
                  style={isActive('/accounts') && !location.search
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/accounts')}
                >
                  <span style={{ marginRight: '6px' }}>📋</span>
                  <span style={{ flex: 1 }}>전체</span>
                </button>
                {rules.map(rule => (
                  <button
                    key={rule.id}
                    style={location.search === `?ruleId=${rule.id}`
                      ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                      : styles.subMenuItem}
                    onClick={() => navigate(`/accounts?ruleId=${rule.id}`)}
                  >
                    <span style={{ marginRight: '6px' }}>⚙️</span>
                    <span style={{ flex: 1 }}>{rule.name}</span>
                    {rule.accountCount > 0 && (
                      <span style={styles.countBadge}>{rule.accountCount}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* A북 현황 - 룰별 서브메뉴 */}
          <div>
            <button
              style={{ ...styles.navItem, ...(location.pathname.startsWith('/a-book') ? styles.navItemActive : {}) }}
              onClick={() => { setABookOpen(!aBookOpen); if (!aBookOpen) navigate('/a-book'); }}
            >
              <span style={styles.navIcon}>🔴</span>
              <span style={{ flex: 1 }}>A북 현황</span>
              <span style={{ fontSize: '10px', opacity: 0.5 }}>{aBookOpen ? '▲' : '▼'}</span>
            </button>
            {aBookOpen && (
              <div style={styles.subMenu}>
                <button
                  style={location.pathname === '/a-book' && !location.search
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/a-book')}
                >
                  <span style={{ marginRight: '6px' }}>📋</span>
                  <span style={{ flex: 1 }}>전체</span>
                </button>
                {rules.map(rule => (
                  <button
                    key={rule.id}
                    style={location.search === `?ruleId=${rule.id}` && location.pathname === '/a-book'
                      ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                      : styles.subMenuItem}
                    onClick={() => navigate(`/a-book?ruleId=${rule.id}`)}
                  >
                    <span style={{ marginRight: '6px' }}>🔴</span>
                    <span style={{ flex: 1 }}>{rule.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            style={isActive('/rules') ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem}
            onClick={() => navigate('/rules')}
          >
            <span style={styles.navIcon}>⚙️</span>
            <span>전환 룰 관리</span>
          </button>

          {/* 수익 통계 - 서브메뉴 */}
          <div>
            <button
              style={{ ...styles.navItem, ...(location.pathname.startsWith('/statistics') ? styles.navItemActive : {}) }}
              onClick={() => { setStatsOpen(!statsOpen); if (!statsOpen) navigate('/statistics/accounts'); }}
            >
              <span style={styles.navIcon}>📊</span>
              <span style={{ flex: 1 }}>수익 통계</span>
              <span style={{ fontSize: '10px', opacity: 0.5 }}>{statsOpen ? '▲' : '▼'}</span>
            </button>
            {statsOpen && (
              <div style={styles.subMenu}>
                <button
                  style={isActive('/statistics/accounts')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/statistics/accounts')}
                >
                  <span style={{ marginRight: '6px' }}>👤</span>
                  <span style={{ flex: 1 }}>계좌별 손익</span>
                </button>
                <button
                  style={isActive('/statistics/rules')
                    ? { ...styles.subMenuItem, ...styles.subMenuItemActive }
                    : styles.subMenuItem}
                  onClick={() => navigate('/statistics/rules')}
                >
                  <span style={{ marginRight: '6px' }}>⚙️</span>
                  <span style={{ flex: 1 }}>전환 룰별 수익</span>
                </button>
              </div>
            )}
          </div>

          {/* 패턴 분석 */}
          <div>
            <button
              style={{ ...styles.navItem, ...(isPatternActive ? styles.navItemActive : {}) }}
              onClick={() => setPatternOpen(!patternOpen)}
            >
              <span style={styles.navIcon}>🧠</span>
              <span style={{ flex: 1 }}>패턴 분석</span>
              <span style={{ fontSize: '10px', opacity: 0.5 }}>{patternOpen ? '▲' : '▼'}</span>
            </button>
            {patternOpen && (
              <div style={styles.subMenu}>
                {[
                  { path: '/pattern/short-high-winrate', label: '단기 고승률형', icon: '⚡' },
                  { path: '/pattern/long-stable', label: '장기 안정수익형', icon: '📈' },
                  { path: '/pattern/high-payoff', label: '고Payoff형', icon: '🎯' },
                  { path: '/pattern/normal-loss', label: '일반 손실형', icon: '📉' },
                  { path: '/pattern/recommendation', label: 'Monitor DB 추천', icon: '🤖' },
                ].map(child => (
                  <button
                    key={child.path}
                    style={isActive(child.path) ? { ...styles.subMenuItem, ...styles.subMenuItemActive } : styles.subMenuItem}
                    onClick={() => navigate(child.path)}
                  >
                    <span style={{ marginRight: '6px' }}>{child.icon}</span>
                    {child.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.userInfo}>
            <div style={styles.userAvatar}>{user?.name?.[0] || 'A'}</div>
            <div>
              <div style={styles.userName}>{user?.name || 'Admin'}</div>
              <div style={styles.userRole}>{user?.role || 'SUPER'}</div>
            </div>
          </div>
          <button style={styles.logoutBtn} onClick={handleLogout}>로그아웃</button>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.content}>{children}</div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', minHeight: '100vh', background: '#0f0f1a', fontFamily: "'Pretendard', -apple-system, sans-serif" },
  sidebar: { width: '220px', minHeight: '100vh', background: 'rgba(255,255,255,0.03)', borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', padding: '24px 16px', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100, overflowY: 'auto' },
  sidebarHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '36px', padding: '0 8px' },
  logo: { width: '40px', height: '40px', background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, color: '#fff', flexShrink: 0 },
  logoTitle: { color: '#fff', fontSize: '16px', fontWeight: 700 },
  logoSub: { color: 'rgba(255,255,255,0.4)', fontSize: '11px' },
  nav: { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.5)', fontSize: '14px', cursor: 'pointer', textAlign: 'left', width: '100%' },
  navItemActive: { background: 'rgba(102,126,234,0.2)', color: '#667eea', fontWeight: 600 },
  navIcon: { fontSize: '16px', width: '20px', textAlign: 'center' },
  subMenu: { marginLeft: '16px', marginTop: '2px', display: 'flex', flexDirection: 'column', gap: '2px' },
  subMenuItem: { display: 'flex', alignItems: 'center', padding: '8px 12px', borderRadius: '8px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.4)', fontSize: '13px', cursor: 'pointer', textAlign: 'left', width: '100%' },
  subMenuItemActive: { background: 'rgba(102,126,234,0.15)', color: '#a78bfa', fontWeight: 600 },
  countBadge: { background: 'rgba(102,126,234,0.3)', color: '#a78bfa', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', fontWeight: 700 },
  sidebarFooter: { borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px', marginTop: '16px' },
  userInfo: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '0 4px' },
  userAvatar: { width: '32px', height: '32px', background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: 700, flexShrink: 0 },
  userName: { color: '#fff', fontSize: '13px', fontWeight: 600 },
  userRole: { color: 'rgba(255,255,255,0.4)', fontSize: '11px' },
  logoutBtn: { width: '100%', padding: '8px', background: 'rgba(255,77,77,0.1)', border: '1px solid rgba(255,77,77,0.2)', borderRadius: '8px', color: '#ff6b6b', fontSize: '13px', cursor: 'pointer' },
  main: { marginLeft: '220px', flex: 1, minHeight: '100vh', overflow: 'auto' },
  content: { padding: '32px', width: '100%', boxSizing: 'border-box' as const },
};

export default Layout;
